import { AuthenticatedImage } from '@components/AuthenticatedImage/AuthenticatedImage';
import { AudioSelectedIcon } from '@components/Icons/AudioSelectedIcon';
import { VideoSelectedIcon } from '@components/Icons/VideoSelectedIcon';
import { PlaceholderGradient } from '@components/util/PlaceholderGradient';
import { generateIdFromString } from '@lib/utils/colorUtils';
import { isAudioFile } from '@lib/utils/fileExtensions';
import classNames from 'classnames';
import { useRef, useState } from 'react';
import css from './Thumbnail.module.scss';

type ThumbnailProps = {
  src?: string;
  alt?: string;
  blur?: number;
  token?: string | null;
  /** Stable seed for the placeholder gradient color (e.g. uniqueId / fingerprint).
   *  When not provided, no gradient placeholder is shown. */
  gradientSeed?: string;
  /** When true, shows an audio icon instead of a video icon on the placeholder.
   *  Falls back to `isAudioFile(alt)` when not provided. */
  isAudio?: boolean;
  /** LQIP data URL from the upload queue. When provided, Thumbnail shows it
   *  blurred behind the main image and hides it once the main image loads. */
  localThumbnail?: string;
};

export const Thumbnail = ({
  src,
  alt,
  blur = 200,
  token,
  gradientSeed,
  isAudio,
  localThumbnail,
}: ThumbnailProps) => {
  const [hasError, setHasError] = useState(false);
  // Tracks whether the main (CDN) image has finished loading.
  // While false and localThumbnail is available, the blurred LQIP stays visible.
  const [mainImageLoaded, setMainImageLoaded] = useState(false);

  // Reset synchronously when src changes (avoids one-frame flash from useEffect).
  const prevSrcRef = useRef(src);
  if (prevSrcRef.current !== src) {
    prevSrcRef.current = src;
    if (mainImageLoaded) setMainImageLoaded(false);
  }

  const lqipId = gradientSeed ? generateIdFromString(gradientSeed) : undefined;

  // When the upload card / processing card has no CDN thumbnail yet, the
  // caller passes the local 256px preview as both `src` and `localThumbnail`.
  // The main image (rendered via the css.thumbnail class below) is sharp by
  // default, but at card size that preview reads as low-quality — apply a
  // stronger blur in that specific case so the user just sees a pleasing
  // smear of the file colour until the CDN thumb takes over.
  const isLocalSrc = !!localThumbnail && src === localThumbnail;

  // Placeholder shown behind loading/errored images: LQIP thumbnail, gradient, and media-type icon.
  // This is what the card showed in the previous lifecycle step (uploading/processing), so
  // transitioning to completed doesn't flash to an empty container while the CDN image loads.
  const placeholder = (showAlways?: boolean) => (
    <>
      {(showAlways || !mainImageLoaded) && localThumbnail && (
        <img
          src={localThumbnail}
          alt={alt}
          className={classNames(css.thumbnail, css.blur, css.lqipOverlay)}
        />
      )}
      {(showAlways || !mainImageLoaded) && lqipId !== undefined && (
        <PlaceholderGradient id={lqipId} blur={blur} />
      )}
    </>
  );

  if (!src || hasError) {
    if (!alt) return null;

    return (
      <div className={css.thumbnailContainer}>
        {placeholder(true)}
        {(isAudio ?? isAudioFile(alt)) ? (
          <AudioSelectedIcon className={css.audioIcon} />
        ) : (
          <VideoSelectedIcon className={css.videoIcon} />
        )}
      </div>
    );
  }

  // External URLs (e.g. YouTube thumbnails) and local data URLs don't need authentication.
  if (!token) {
    return (
      <div className={css.thumbnailContainer}>
        {placeholder()}
        <img
          src={src}
          alt={alt}
          className={classNames(css.thumbnail, isLocalSrc && css.blurLocalMain)}
          onError={() => setHasError(true)}
          onLoad={() => setMainImageLoaded(true)}
        />
      </div>
    );
  }

  return (
    <div className={css.thumbnailContainer}>
      {placeholder()}
      <AuthenticatedImage
        src={src}
        width={'100%'}
        height={'100%'}
        alt={alt}
        className={classNames(css.thumbnail, isLocalSrc && css.blurLocalMain)}
        token={token}
        onError={() => setHasError(true)}
        onLoad={() => setMainImageLoaded(true)}
      />
    </div>
  );
};
